/**
 * Шаблон карточки курса
 */
import { createLogger, LogLevel } from '../utils/logger';
import { getTabColorsForCourse } from '../utils/colors';
import { getCourseLinkById } from '../utils/course-by-id';
import { getYmGoal } from '../utils/helpYmGoals';

// Создаем логгер для карточки курса
const logger = createLogger('CourseCard', LogLevel.INFO);

// Функция форматирования цены
const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('ru-RU').format(price);
};

// Форматирование даты старта (YYYY-MM-DD -> 1 января)
const formatStartDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const day = String(Number(match[3]));
    const month = ruMonthName(Number(match[2]));
    return `${day} ${month}`;
  }
  return dateStr;
};

// Перевод числа в русское название месяца
const ruMonthName = (month: number): string => {
  const months = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
  return months[month-1] || '';
};

// Форматирование длительности (дни -> месяцы)
const formatDuration = (days: number): string => {
  const d = Number(days);
  if (!d || d <= 0) return '—';
  const months = Math.max(1, Math.floor(d/30));
  return `${months} ${pluralMonthRu(months)}`;
};

const pluralMonthRu = (n: number): string => {
  if (n % 10 === 1 && n % 100 !== 11) return 'месяц';
  if ([2,3,4].includes(n%10) && ![12,13,14].includes(n%100)) return 'месяца';
  return 'месяцев';
};

// Функция для проверки пустых значений (аналог PHP empty)
const empty = (value: any): boolean => {
  return value === null || value === undefined || value === '' || value === 0 || value === '0' || value === false;
};

// Обработчик выбора курса
const handleCourseSelect = (courseId: number, event: Event) => {
  event.preventDefault();
  logger.debug(`Выбран курс с ID: ${courseId}`);
  
  // Если есть обработчик глобального события, вызываем его
  if (window.courseCatalog?.onCourseSelect) {
    window.courseCatalog.onCourseSelect(courseId);
  }
};

/**
 * Рендерит превью карточки курса
 */
const renderCoursePreview = (course: any): HTMLElement => {
  if (!course.catalog_cover_url) {
    const preview = document.createElement('div');
    preview.className = 'course__preview';
    return preview;
  }
  
  const preview = document.createElement('div');
  preview.className = 'course__preview';
  
  const img = document.createElement('img');
  img.src = `${course.catalog_cover_url}`;
  img.alt = course.title || '';
  img.loading = 'lazy';
  preview.appendChild(img);
  
  return preview;
};

/**
 * Рендерит информацию о курсе
 */
const renderCourseInfo = (course: any): HTMLElement => {
  const iconsPath = '/wp-content/themes/100points/pages/catalog/assets/icons';
  
  const info = document.createElement('div');
  info.className = 'course__about__info';
  
  if (course.start_at) {
    const p = document.createElement('p');
    const img = document.createElement('img');
    img.src = `${iconsPath}/flag.svg`;
    img.alt = 'Дата начала курса';
    p.appendChild(img);
    p.appendChild(document.createTextNode(` ${formatStartDate(course.start_at)}`));
    info.appendChild(p);
  }
  
  if (course.duration) {
    const p = document.createElement('p');
    const img = document.createElement('img');
    img.src = `${iconsPath}/clock.svg`;
    img.alt = 'Длительность курса';
    p.appendChild(img);
    p.appendChild(document.createTextNode(` ${formatDuration(course.duration)}`));
    info.appendChild(p);
  }
  
  if (course.content_info) {
    const p = document.createElement('p');
    const img = document.createElement('img');
    img.src = `${iconsPath}/book.svg`;
    img.alt = 'Количество уроков';
    p.appendChild(img);
    p.appendChild(document.createTextNode(` ${course.content_info}`));
    info.appendChild(p);
  } else if (course.lessons_count) {
    const p = document.createElement('p');
    const img = document.createElement('img');
    img.src = `${iconsPath}/book.svg`;
    img.alt = 'Количество уроков';
    p.appendChild(img);
    p.appendChild(document.createTextNode(` ${course.lessons_count} уроков`));
    info.appendChild(p);
  }
  
  if (course.format_info) {
    const p = document.createElement('p');
    const img = document.createElement('img');
    img.src = `${iconsPath}/file.svg`;
    img.alt = 'Особенности курса';
    p.appendChild(img);
    p.appendChild(document.createTextNode(` ${course.format_info}`));
    info.appendChild(p);
  }
  
  return info;
};

/**
 * Рендерит кнопки действий для курса
 */
const renderCourseActions = (course: any): HTMLElement => {
  const orderBaseUrl = 'https://study.100points.ru/order/?course_id=';

  const actions = document.createElement('div');
  actions.className = 'course__buy__actions';

  // --- ЦЕНА ---
  let minPrice: any = null;
  if (Array.isArray(course.tariffs) && course.tariffs.length > 0) {
    minPrice = course.tariffs
      .map((t: any) => t.new_price_per_module || t.price_per_module || t.new_price_total || t.price_total)
      .filter(Boolean)
      .map((n: any) => Number(n))
      .sort((a: any, b: any) => a - b)[0];
  }

  // --- КНОПКИ ---
  if (Array.isArray(course.tariffs) && course.tariffs.length > 0) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'course__buy__action course__buy__tariffs btn-clrd orange';
    button.dataset.courseId = String(course.id);
    button.textContent = 'Выбрать тариф';
    button.setAttribute('data-action', 'open-tariff-modal');
    
    // Добавляем Яндекс.Метрику
    const ymGoalTariff = getYmGoal('tariff');
    if (ymGoalTariff) {
      button.setAttribute('data-ym-goal', ymGoalTariff);
      button.setAttribute('data-ym-params', JSON.stringify({ course_id: String(course.id) }));
    }
    
    actions.appendChild(button);
  }
  // Используем getCourseLinkById для получения ссылки на курс
  const courseLink = getCourseLinkById(course.id);
  if (courseLink) {
    const a = document.createElement('a');
    a.className = 'course__buy__details btn-brdrd black';
    a.href = courseLink;
    a.textContent = 'О курсе';
    
    // Добавляем Яндекс.Метрику
    const ymGoalAbout = getYmGoal('about');
    if (ymGoalAbout) {
      a.setAttribute('data-ym-goal', ymGoalAbout);
      a.setAttribute('data-ym-params', JSON.stringify({ course_id: String(course.id) }));
    }
    
    actions.appendChild(a);
  }

  return actions;
};

/**
 * Основной шаблон карточки курса
 */
export const createCourseCard = (course: any): HTMLElement => {
  logger.info('Рендер createCourseCard:', course);
  if (!course || !course.id) {
    logger.warn('Попытка отрисовать некорректные данные курса:', course);
    const card = document.createElement('div');
    card.className = 'course course-error';
    card.textContent = 'Ошибка данных курса';
    return card;
  }
  
  // --- ЦЕНА ---
  let minPrice: any = null;
  if (course.discounted_price || course.full_price) {
    minPrice = course.discounted_price || course.full_price;
  } else if (Array.isArray(course.tariffs) && course.tariffs.length > 0) {
    minPrice = course.tariffs
      .map((t: any) => t.new_price_per_module || t.price_per_module || t.new_price_total || t.price_total)
      .filter(Boolean)
      .map((n: any) => Number(n))
      .sort((a: any, b: any) => a - b)[0];
  }

  // Получаем цвета таба по новой логике (эксперт)
  const { tab_color: card_color, tab_text_color } = getTabColorsForCourse(course);
  
  const card = document.createElement('div');
  card.className = 'course';
  card.setAttribute('data-id', String(course.id));
  
  const preview = renderCoursePreview(course);
  card.appendChild(preview);
  
  const content = document.createElement('div');
  content.className = 'course__content';
  
  const about = document.createElement('div');
  about.className = 'course__about';
  if (!course.is_ready_to_buy || course.is_ready_to_buy == 0) {
    about.classList.add('blocked');
  }
  
  if (course.short_title) {
    const preTitle = document.createElement('div');
    preTitle.className = 'course__about__preTitle';
    
    if (course.id == 1791) {
      const p = document.createElement('p');
      p.textContent = 'Курс подготовки к поступлению';
      preTitle.appendChild(p);
    } else if (course.discipline?.name || course.discipline?.display_name) {
      const p = document.createElement('p');
      p.textContent = `Курс подготовки к ${course.goal.name}  ${course.discipline?.display_name}`;
      preTitle.appendChild(p);
    }
    
    if (course.short_description) {
      const tooltip = document.createElement('div');
      tooltip.className = 'tooltip';
      
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '16');
      svg.setAttribute('height', '17');
      svg.setAttribute('viewBox', '0 0 16 17');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      
      const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path1.setAttribute('d', 'M8 0.5C3.6 0.5 0 4.1 0 8.5C0 12.9 3.6 16.5 8 16.5C12.4 16.5 16 12.9 16 8.5C16 4.1 12.4 0.5 8 0.5ZM8 14.5C4.7 14.5 2 11.8 2 8.5C2 5.2 4.7 2.5 8 2.5C11.3 2.5 14 5.2 14 8.5C14 11.8 11.3 14.5 8 14.5Z');
      path1.setAttribute('fill', '#A0A0A0');
      svg.appendChild(path1);
      
      const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path2.setAttribute('d', 'M9 7.50049H7V12.5005H9V7.50049ZM9 4.50049H7V6.50049H9V4.50049Z');
      path2.setAttribute('fill', '#A0A0A0');
      svg.appendChild(path2);
      
      tooltip.appendChild(svg);
      
      const tooltipText = document.createElement('div');
      tooltipText.className = 'tooltip__text';
      
      const pTooltip = document.createElement('p');
      pTooltip.textContent = course.short_description;
      tooltipText.appendChild(pTooltip);
      
      tooltip.appendChild(tooltipText);
      
      preTitle.appendChild(tooltip);
    }
    
    about.appendChild(preTitle);
  }
  
  const pTitle = document.createElement('h3');
  pTitle.className = 'course__about__title';
  pTitle.textContent = course.short_title || course.title;
  about.appendChild(pTitle);
  
  const tabs = document.createElement('div');
  tabs.className = 'course__about__tabs';
  
  if (course.classes && course.classes[0]?.grade) {
    const pTab = document.createElement('p');
    pTab.style.backgroundColor = card_color;
    pTab.style.color = tab_text_color;
    pTab.textContent = `${course.classes[0].grade} класс`;
    tabs.appendChild(pTab);
  }
  
  if (course.goal && course.goal.name) {
    const pTab = document.createElement('p');
    pTab.style.backgroundColor = card_color;
    pTab.style.color = tab_text_color;
    pTab.textContent = course.goal.name;
    tabs.appendChild(pTab);
  }
  
  if (course.discipline && course.discipline.name && course.id != 1791) {
    const pTab = document.createElement('p');
    pTab.style.backgroundColor = card_color;
    pTab.style.color = tab_text_color;
    pTab.textContent = course.discipline.name;
    tabs.appendChild(pTab);
  }
  
  about.appendChild(tabs);
  
  about.appendChild(renderCourseInfo(course));
  
  content.appendChild(about);
  
  const buy = document.createElement('div');
  buy.className = 'course__buy';
  
  // Если курс закрыт для набора
  if (empty(course.is_ready_to_buy) || course.is_ready_to_buy == 0) {
    // Выводим сообщение "Набор на курс закрыт"
    const closedMessage = document.createElement('p');
    closedMessage.className = 'closedMessage';
    closedMessage.textContent = 'Набор на курс закрыт';
    buy.appendChild(closedMessage);
  } else {
    // Иначе выводим цену
    const prices = document.createElement('div');
    prices.className = 'course__buy__prices';
    
    if (minPrice) {
      const divPrice = document.createElement('div');
      divPrice.className = 'regular-price';
      const pPrice = document.createElement('p');
      pPrice.innerHTML = 'от';
      const spanPrice = document.createElement('span');
      spanPrice.className = 'priceValue';
      spanPrice.textContent = formatPrice(minPrice) + ' ₽';
      pPrice.appendChild(spanPrice);
      const spanPerMonth = document.createElement('span');
      spanPerMonth.textContent = '/ месяц';
      pPrice.appendChild(spanPerMonth);
      divPrice.appendChild(pPrice);
      prices.appendChild(divPrice);
    }
    
    buy.appendChild(prices);
  }
  
  const actions = document.createElement('div');
  actions.className = 'course__buy__actions';
  if (empty(course.is_ready_to_buy) || course.is_ready_to_buy == 0) {
    const prelistLink = (course.goal && course.goal.slug === 'ege')
      ? 'https://t.me/Predspisokege_100b_bot'
      : 'https://t.me/Predspisokoge_100b_bot';
    const a = document.createElement('a');
    a.href = prelistLink;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.className = 'btn-brdrd black grow';
    a.textContent = 'Попасть в предсписок';
    actions.appendChild(a);
  } else {
    // Обычные действия (кнопка "Выбрать тариф")
    if (Array.isArray(course.tariffs) && course.tariffs.length > 0) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'course__buy__action course__buy__tariffs btn-clrd orange';
      button.setAttribute('data-action', 'open-tariff-modal');
      button.dataset.courseId = String(course.id);
      button.textContent = 'Выбрать тариф';
      
      // Добавляем Яндекс.Метрику
      const ymGoalTariff = getYmGoal('tariff');
      if (ymGoalTariff) {
        button.setAttribute('data-ym-goal', ymGoalTariff);
        button.setAttribute('data-ym-params', JSON.stringify({ course_id: String(course.id) }));
      }
      
      actions.appendChild(button);
    }
  }
  // Ссылка "О курсе"
  const courseLink = getCourseLinkById(course.id);
  if (courseLink) {
    const a = document.createElement('a');
    a.className = 'course__buy__details btn-brdrd black';
    a.href = courseLink;
    a.textContent = 'О курсе';
    
    // Добавляем Яндекс.Метрику
    const ymGoalAbout = getYmGoal('about');
    if (ymGoalAbout) {
      a.setAttribute('data-ym-goal', ymGoalAbout);
      a.setAttribute('data-ym-params', JSON.stringify({ course_id: String(course.id) }));
    }
    
    actions.appendChild(a);
  }
  buy.appendChild(actions);
  
  content.appendChild(buy);
  
  card.appendChild(content);
  
  return card;
}; 